define("ROOM4.CCAWPdpLightbox.CCAWPdpLightbox", [
  "ProductDetails.ImageGallery.View",
  "ROOM4.CCAWPhotoSwipeLoader.CCAWPhotoSwipeLoader",
  "Utilities.ResizeImage",
  "jQuery",
  "underscore",
], function (
  ImageGalleryView,
  PhotoSwipeLoader,
  resizeImage,
  jQuery,
  _
) {
  "use strict";

  var LIGHTBOX_IMAGE_MAX_WIDTH = 1600;
  var LIGHTBOX_IMAGE_MAX_HEIGHT = 1600;
  // matches bxSlider's default swipeThreshold
  var DRAG_THRESHOLD = 10;
  var EVENT_NAMESPACE = ".ccawPdpLightbox";

  // Scales width x height to fit the box preserving aspect ratio, upscaling
  // if needed — the same fitting the NetSuite image resize service applies,
  // so the result reproduces a resize variant's intrinsic dimensions.
  function fitToBox(width, height, maxWidth, maxHeight) {
    var scale;

    if (!(width > 0) || !(height > 0)) {
      return { width: maxWidth, height: maxHeight };
    }

    scale = Math.min(maxWidth / width, maxHeight / height);

    return {
      width: Math.round(width * scale),
      height: Math.round(height * scale),
    };
  }

  function getResizeBox(resizedUrl) {
    var widthMatch = /[?&]resizew=(\d+)/i.exec(resizedUrl);
    var heightMatch = /[?&]resizeh=(\d+)/i.exec(resizedUrl);

    return {
      width: widthMatch
        ? parseInt(widthMatch[1], 10)
        : LIGHTBOX_IMAGE_MAX_WIDTH,
      height: heightMatch
        ? parseInt(heightMatch[1], 10)
        : LIGHTBOX_IMAGE_MAX_HEIGHT,
    };
  }

  // The PDP model images carry no pixel dimensions and PhotoSwipe requires
  // them up front (async correction after load is unsupported and re-renders
  // slides). They are derived instead: aspect ratio from the already-rendered
  // slider <img>, scaled into the zoom variant's box parsed from its own URL.
  function buildDataSource(view) {
    var $slideImages = view.$("[data-zoom]:not(.bx-clone) img");

    return _.map(view.images || [], function (image, index) {
      var slideImage = $slideImages.get(index);
      var src = resizeImage(image.url, "zoom");
      var box = getResizeBox(src);
      var size = fitToBox(
        slideImage && slideImage.naturalWidth,
        slideImage && slideImage.naturalHeight,
        box.width,
        box.height
      );

      return {
        src: src,
        alt: image.altimagetext || "",
        width: size.width,
        height: size.height,
      };
    });
  }

  function openLightbox(view, container) {
    var dataSource = buildDataSource(view);
    // parseInt because SDBProductModifications' custom pager feeds bxSlider
    // an index read from a data-slide-index attribute, so getCurrentSlide()
    // can return a string — which PhotoSwipe breaks on ("2" + 1 === "21")
    var index =
      view.$slider && view.$slider.getCurrentSlide
        ? parseInt(view.$slider.getCurrentSlide(), 10)
        : 0;

    if (!dataSource.length) {
      return;
    }

    if (isNaN(index) || index < 0 || index >= dataSource.length) {
      index = 0;
    }

    PhotoSwipeLoader.load(container).then(
      function (PhotoSwipeApi) {
        if (!view.ccawPdpLightbox) {
          view.ccawPdpLightbox = PhotoSwipeApi.createLightbox({
            // scopes CSS to this lightbox, e.g. the white backdrop that
            // transparent product PNGs need
            mainClass: "ccaw-pdp-lightbox-pswp",
          });
          view.ccawPdpLightbox.init();
        }
        view.ccawPdpLightbox.options.dataSource = dataSource;
        view.ccawPdpLightbox.loadAndOpen(index);
      },
      function (error) {
        console.error("CCAWPdpLightbox: failed to load PhotoSwipe", error);
      }
    );
  }

  // Delegated, namespaced handlers instead of the view's events hash:
  // SDBProductModifications replaces that hash wholesale and mount order
  // between extensions is not guaranteed.
  function attachLightboxHandlers(view, container) {
    var dragStart = null;

    view.$el.addClass("ccaw-pdp-lightbox-enabled");
    view.$el.off(EVENT_NAMESPACE);

    view.$el.on(
      "pointerdown" + EVENT_NAMESPACE + " touchstart" + EVENT_NAMESPACE,
      "[data-zoom]",
      function (event) {
        var point = event.originalEvent.changedTouches
          ? event.originalEvent.changedTouches[0]
          : event.originalEvent;
        dragStart = { x: point.pageX, y: point.pageY };
      }
    );

    view.$el.on("click" + EVENT_NAMESPACE, "[data-zoom]", function (event) {
      var moved = dragStart
        ? Math.max(
            Math.abs(event.pageX - dragStart.x),
            Math.abs(event.pageY - dragStart.y)
          )
        : 0;

      // finishing a bxSlider drag-swipe over the image must not open it
      if (moved >= DRAG_THRESHOLD) {
        return;
      }

      event.preventDefault();
      openLightbox(view, container);
    });
  }

  function patchImageGalleryView(container) {
    _.extend(ImageGalleryView.prototype, {
      initialize: _.wrap(ImageGalleryView.prototype.initialize, function (fn) {
        fn.apply(this, _.toArray(arguments).slice(1));
        // the gate initSliderZoom checks; overriding it here wins over
        // SC.CONFIGURATION.isZoomEnabled and over SDBProductModifications'
        // initZoom wrapper regardless of extension mount order
        this.isZoomEnabled = false;
      }),

      initSliderZoom: _.wrap(ImageGalleryView.prototype.initSliderZoom, function (
        fn
      ) {
        fn.apply(this, _.toArray(arguments).slice(1));
        // fires on every render path: layout afterAppendView, matrix-option
        // model change and quick-view modals
        attachLightboxHandlers(this, container);
      }),

      destroy: _.wrap(ImageGalleryView.prototype.destroy, function (fn) {
        this.$el.off(EVENT_NAMESPACE);
        if (this.ccawPdpLightbox) {
          this.ccawPdpLightbox.destroy();
          this.ccawPdpLightbox = null;
        }
        fn.apply(this, _.toArray(arguments).slice(1));
      }),
    });
  }

  return {
    mountToApp: function mountToApp(container) {
      var Environment = container.getComponent("Environment");
      var enabled =
        Environment && Environment.getConfig("CCAWPdpLightbox.enabled") === true;

      // when the lightbox cannot load (SEO page generator, no ES module
      // support) leave the stock hover-zoom behavior untouched
      if (!enabled || !PhotoSwipeLoader.canLoad(container)) {
        return;
      }

      patchImageGalleryView(container);
    },
  };
});
